import React from 'react';
import { RocketLaunchIcon, CogIcon, UsersIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';

const Stage3 = () => {
  return (
    <PlaybookSection
      title="Stage 3: Operationalising Further"
      icon={<CogIcon className="h-7 w-7 text-primary-light" />}
      heading="Normalize Without Becoming the AI Police"
    >
      <div className="space-y-8">
        {/* TL;DR */}
        <ExpandableCallout 
          title="TL;DR - What you'll accomplish in this stage"
          type="info"
          defaultOpen={true}
        >
          <ul className="space-y-2 text-gray-300">
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">1.</span>
              <span>Address fear and resistance with clear communication and myth-busting</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">2.</span>
              <span>Integrate AI skills into your existing career development frameworks</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">3.</span>
              <span>Create lightweight guardrails that enable rather than restrict</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">4.</span>
              <span>Build sustainable systems for sharing, tracking, and improving AI adoption</span>
            </li>
          </ul>
        </ExpandableCallout>

        {/* What do I need to DO */}
        <ExpandableCallout 
          title="What do I need to DO in this stage?"
          type="tip"
          defaultOpen={true}
        >
          <ol className="space-y-3 text-gray-300">
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">1.</span>
              <span>Run a myth-busting session about AI and job security</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">2.</span>
              <span>Add AI fluency to career development conversations and frameworks</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">3.</span>
              <span>Create an AI Use Ladder for self-assessment and progression</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">4.</span>
              <span>Set up tracking systems for AI tools and impact measurement</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">5.</span>
              <span>Establish regular sharing mechanisms and case study collection</span>
            </li>
          </ol>
        </ExpandableCallout>

        {/* Introduction */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">The Challenge</h3>
          <p className="text-gray-300 mb-4">
            You've got your first GPTs in the wild. People are tinkering. Energy is up.
          </p>
          <p className="text-gray-300">
            Now comes the hard part: normalising this across the org without becoming the AI police.
          </p>
        </PlaybookCard>

        {/* Managing Fear and Resistance */}
        <PlaybookCard>
          <div className="flex items-start mb-4">
            <UsersIcon className="h-6 w-6 text-primary-light mr-3 mt-1 flex-shrink-0" />
            <h3 className="text-xl font-semibold text-white">Manage Fear, Resistance, and Mindset Shifts</h3>
          </div>
          
          <p className="text-gray-300 mb-4">
            Even the most optimistic teams have pockets of quiet panic.
          </p>
          
          <ul className="space-y-2 text-gray-300 mb-6">
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>"Will AI take my job?"</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>"What if I break something?"</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>"I'm already behind—how am I supposed to keep up?"</span>
            </li>
          </ul>

          <p className="text-gray-300 mb-6">
            You don't need to squash the fear. Just name it, normalise it, and reframe it.
          </p>

          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
            <h4 className="font-semibold text-white mb-3">Try This: "AI Won't Take Your Job, But..."</h4>
            <p className="text-gray-300 mb-4">
              Run a short myth-busting session as part of an all-hands or function kickoff.
            </p>
            
            <div className="space-y-4">
              <div className="bg-gray-900/50 rounded p-3">
                <p className="text-white font-medium">Slide 1:</p>
                <p className="text-red-400">❌ AI will replace you</p>
                <p className="text-green-400">✅ Someone using AI <em>more effectively</em> than you might</p>
              </div>
              
              <div className="bg-gray-900/50 rounded p-3">
                <p className="text-white font-medium">Slide 2:</p>
                <p className="text-gray-300">"You don't need to be an AI expert. You just need to be curious and willing to try."</p>
              </div>
              
              <div className="bg-gray-900/50 rounded p-3">
                <p className="text-white font-medium">Slide 3:</p>
                <p className="text-gray-300">"Here's how we'll help."</p>
              </div>
            </div>
            
            <p className="text-gray-300 mt-4">
              <strong className="text-primary-light">Frame it as leverage, not a threat.</strong> Give people small experiments, 
              low-stakes places to start, and stories of peers using it well.
            </p>
          </div>
        </PlaybookCard>

        {/* Career Development Integration */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">Bake AI Skills Into Career Development</h3>
          <p className="text-gray-300 mb-4">
            You don't need a new competency model. You just need to reflect where this shows up in <em>existing</em> skills.
          </p>

          <h4 className="font-semibold text-white mb-4">How to Do It:</h4>
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 text-white font-medium">Level</th>
                  <th className="text-left py-3 text-white font-medium">Where AI Fits</th>
                </tr>
              </thead>
              <tbody className="text-gray-300">
                <tr className="border-b border-gray-800">
                  <td className="py-3 font-medium">ICs</td>
                  <td className="py-3">"Uses AI to work faster, smarter, and reduce manual effort"</td>
                </tr>
                <tr className="border-b border-gray-800">
                  <td className="py-3 font-medium">People Managers</td>
                  <td className="py-3">"Coaches team on integrating AI into workflow"</td>
                </tr>
                <tr>
                  <td className="py-3 font-medium">Leaders</td>
                  <td className="py-3">"Drives AI opportunity spotting across their domain"</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mb-4">
            <p className="text-white font-medium">
              Bonus move: Add "AI fluency" as a line in PDP templates.
            </p>
            <p className="text-gray-300 text-sm mt-2">
              E.g. <em>"What's one workflow you want to AI-ify this quarter?"</em>
            </p>
          </div>

          <p className="text-gray-300 text-sm">
            <strong>Tip:</strong> Recognise visible AI adoption in performance convos—but don't make it a blunt KPI yet.
          </p>
        </PlaybookCard>

        {/* AI Use Ladder */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">1. AI Use Ladder (aka: How Fluent Are We, Really?)</h3>
          <p className="text-gray-300 mb-4">
            Give people a simple way to self-assess where they are and where they're going:
          </p>

          <div className="space-y-4 mb-6">
            <div className="flex items-start">
              <span className="text-2xl mr-4">👀</span>
              <div>
                <p className="text-white font-medium">Curious</p>
                <p className="text-gray-300 text-sm">"I've tried ChatGPT a couple times"</p>
                <p className="text-gray-400 text-xs">Example: Asking it to summarise an article</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <span className="text-2xl mr-4">🛠️</span>
              <div>
                <p className="text-white font-medium">Tinkering</p>
                <p className="text-gray-300 text-sm">"I'm using GPTs to improve my own workflow"</p>
                <p className="text-gray-400 text-xs">Example: Drafting emails, summarising notes</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <span className="text-2xl mr-4">🤖</span>
              <div>
                <p className="text-white font-medium">Integrated</p>
                <p className="text-gray-300 text-sm">"I've created a workflow or internal tool others use"</p>
                <p className="text-gray-400 text-xs">Example: GPT for auto-tagging or insights</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <span className="text-2xl mr-4">🧭</span>
              <div>
                <p className="text-white font-medium">Strategic</p>
                <p className="text-gray-300 text-sm">"I'm spotting AI opportunities across my function"</p>
                <p className="text-gray-400 text-xs">Example: Reworking ops flows to be AI-first</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
            <h4 className="font-semibold text-white mb-3">You can use this in:</h4>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>Onboarding</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>PDPs</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>Leadership dev check-ins</span>
              </li>
            </ul>
            <p className="text-gray-300 mt-3 text-sm">
              It creates a shared language for capability <em>without</em> turning it into a certification treadmill.
            </p>
          </div>
        </PlaybookCard>

        {/* Tracking Template */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">2. Tracking Template</h3>
          <p className="text-gray-300 mb-4">
            Give people a lightweight way to track progress and impact.
          </p>

          

          <div className="mt-6 bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
            <h4 className="font-semibold text-white mb-3">Track:</h4>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>Team GPTs (name, owner, use case, adoption)</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>Status (v1, in use, needs upgrade, archived)</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>Wins and blockers</span>
              </li>
            </ul>
            <p className="text-gray-300 mt-3 text-sm">
              <strong>Why it helps:</strong> Creates internal visibility + avoids duplicate efforts + gives you proof of impact.
            </p>
          </div>
        </PlaybookCard>

        {/* Case Study Collection */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">3. Case Study Snapshot in Slack</h3>
          <p className="text-gray-300 mb-4">
            Two-sentence slack stories from early adopters. They don't have to be revolutionary—just <em>real</em>.
          </p>

          <CopyToNotion title="Case Study Template for Slack" slack>
            <div className="space-y-4">
              <p className="font-bold">[Tool Name] GPT</p>
              <p>"We built a simple GPT that [describes what it does]. Saved us ~[time/effort] and helped [specific benefit or example]."</p>
              
              <hr className="border-gray-600" />
              
              <p className="font-bold">Example:</p>
              <p className="font-bold">Customer Support GPT</p>
              <p>"We built a simple GPT that reads inbound tickets and suggests tone-matched first responses. Saved us ~3 hours/week and helped onboard a new team member 2x faster."</p>
            </div>
          </CopyToNotion>
        </PlaybookCard>

        {/* Lightweight Guardrails */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">Set Lightweight Guardrails (Without Slowing Teams Down)</h3>
          <p className="text-gray-300 mb-4">
            You already have policies. You don't need a 32-page AI addendum.
          </p>
          
          <p className="text-gray-300 mb-6">Start with 3 Golden Rules:</p>
          
          <div className="space-y-4 mb-6">
            <div className="flex items-start">
              <span className="text-primary-light font-bold mr-3">1.</span>
              <div>
                <p className="text-white font-medium">Use judgement</p>
                <p className="text-gray-300 text-sm">Just because it's fast doesn't mean it's good.</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <span className="text-primary-light font-bold mr-3">2.</span>
              <div>
                <p className="text-white font-medium">Protect sensitive data</p>
                <p className="text-gray-300 text-sm">No PII, customer info, or confidential IP in public models.</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <span className="text-primary-light font-bold mr-3">3.</span>
              <div>
                <p className="text-white font-medium">Check before shipping</p>
                <p className="text-gray-300 text-sm">If it impacts customers, a human signs off.</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50 mb-4">
            <h4 className="font-semibold text-white mb-3">Drop it into a "Startup AI Playbook" one-pager</h4>
            <p className="text-gray-300 mb-3">Include:</p>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>Green / Yellow / Red zones</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>Link to your best GPTs</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>Slack channel for AI wins / Qs / support</span>
              </li>
            </ul>
          </div>

          <ToolCallout
            label="AI Policy Template"
            href="https://www.openverse.fyi/resource/ai-policy-template"
            description="Ready-to-use AI policy template for startups"
          />

          <p className="text-gray-300 mt-4 text-sm">
            <strong>When in doubt:</strong> ship, share, and improve. Escalate only when the risk is real.
          </p>
        </PlaybookCard>

        {/* Additional Embedding Ideas */}
        <ExpandableCallout 
          title="Additional Ideas for Embedding AI Culture"
          type="tip"
          defaultOpen={true}
        >
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-white mb-3">Monthly AI Show & Tell</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>10-minute demo slots during all-hands</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Focus on impact, not tech specs</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Celebrate "scrappy but useful" over "perfect"</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-3">AI Learning Lunch Series</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Peer-to-peer teaching sessions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Focus on practical skills, not theory</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Hands-on workshop format</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-3">AI Buddy System</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Pair AI-curious people with AI-confident people</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Monthly check-ins and goal setting</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Mutual accountability and support</span>
                </li>
              </ul>
            </div>
          </div>
        </ExpandableCallout>

        {/* Success Metrics */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">How to Measure Success</h3>
          <p className="text-gray-300 mb-4">
            Track these leading indicators rather than vanity metrics:
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-white mb-3">Leading Indicators</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Number of GPTs in active use</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>People moving up the AI Use Ladder</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Cross-team sharing and collaboration</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Frequency of AI mentions in standups</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-3">Impact Indicators</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Time saved on repetitive tasks</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Quality improvements in outputs</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Reduced context switching</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Employee confidence in AI topics</span>
                </li>
              </ul>
            </div>
          </div>
        </PlaybookCard>

        {/* Outcome */}
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Stage 3 Outcome</h3>
          <p className="text-gray-300 mb-4">
            By the end of this stage, you should have:
          </p>
          <ul className="space-y-2 text-gray-300 mb-4">
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>Addressed fears and resistance with clear, empathetic communication</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>Integrated AI fluency into career development conversations</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>Created sustainable systems for tracking and sharing AI adoption</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>Established lightweight guardrails that enable rather than restrict</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>Built cultural momentum that will sustain AI adoption long-term</span>
            </li>
          </ul>
          <p className="text-gray-300">
            <strong>Next:</strong> Check out the <a href="/playbooks/leverage-ai-for-you-and-your-org/useful-resources" className="text-primary hover:text-primary-light">Useful Resources</a> for templates, tools, and policies to support your AI journey.
          </p>
        </div>
      </div>
    </PlaybookSection>
  );
};

export default Stage3; 